/*
 * Pure helpers for the football-intelligence views.
 *
 * Rules every function here follows:
 *  - Inputs are provider actuals returned by /api/football/intelligence/*.
 *  - Outputs are sums, means, counts or geometric classifications of those
 *    inputs. Nothing is estimated, smoothed into new values, or inferred.
 *  - Missing data stays null so the UI can say "unavailable".
 */

export const PITCH = { length: 105, width: 68 };

// 16.5 m penalty area on a 105 x 68 pitch, attacking towards x = 105.
const BOX = { minX: 88.5, minY: 13.84, maxY: 54.16 };

/* ------------------------------------------------------------------ */
/* Metric labels, hints and grouping                                    */
/* ------------------------------------------------------------------ */

const LABELS = {
  xgf: "Expected goals (xG)",
  xga: "xG against (xGA)",
  npxgf: "Non-penalty xG",
  npxga: "Non-penalty xGA",
  npxg: "Non-penalty xG",
  open_play_xgf: "Open-play xG",
  set_piece_xgf: "Set-piece xG",
  xg_per_shot: "xG per shot",
  shots_for: "Shots",
  shots_against: "Shots faced",
  total_shots: "Shots",
  blocked_shots_for: "Blocked shots (for)",
  blocked_shots_against: "Blocked shots (against)",
  goals: "Goals",
  conceded: "Goals conceded",
  clean_sheet: "Clean sheets",
  assists: "Assists",
  xag: "Expected assisted goals (xAG)",
  key_passes: "Key passes",
  corners_for: "Corners won",
  corners_against: "Corners conceded",
  fouls: "Fouls",
  fouls_for: "Fouls (for)",
  fouls_against: "Fouls (against)",
  yellow_cards: "Yellow cards",
  red_cards: "Red cards",
  yellow_cards_for: "Yellow cards (for)",
  yellow_cards_against: "Yellow cards (against)",
  red_cards_for: "Red cards (for)",
  red_cards_against: "Red cards (against)",
  passes_for: "Passes (for)",
  passes_against: "Passes (against)",
  passes_attempted: "Passes attempted",
  accurate_passes: "Passes completed",
  progressive_passes: "Progressive passes",
  progressive_carries: "Progressive carries",
  xt_total: "Expected threat (xT)",
  tackles: "Tackles",
  interceptions: "Interceptions",
  recoveries: "Ball recoveries",
  "territory.possession_pct": "Possession",
  "territory.field_tilt": "Field tilt",
  "territory.box_entries": "Box entries",
  "territory.final_third_entries": "Final-third entries",
  "territory.avg_action_x": "Average action height",
  "defending.ppda": "PPDA",
  "defending.tackles": "Tackles",
  "defending.interceptions": "Interceptions",
  "defending.clearances": "Clearances",
  "defending.blocks": "Blocks",
  "defending.high_turnovers": "High turnovers",
  "defending.counterpress_regains_5s": "Counter-press regains (5 s)",
  "defending.aerials_won": "Aerials won",
  "defending.avg_defensive_action_x": "Average defensive-action height",
  "passing.passes": "Passes",
  "passing.pass_accuracy": "Pass accuracy",
  "passing.progressive_passes": "Progressive passes",
  "passing.passes_into_box": "Passes into box",
  "passing.key_passes": "Key passes",
  "passing.crosses": "Crosses",
  "passing.through_balls": "Through balls",
  "passing.switches": "Switches of play",
  "creation.chances_created": "Chances created",
  "creation.sca": "Shot-creating actions",
  "creation.gca": "Goal-creating actions",
  "creation.xag": "Expected assisted goals (xAG)",
  "carrying.progressive_carries": "Progressive carries",
  "carrying.carries_into_box": "Carries into box",
  "carrying.carries_into_final_third": "Carries into final third",
  "carrying.take_ons": "Take-ons attempted",
  "carrying.take_ons_won": "Take-ons won",
  "possession_value.xt_total": "Expected threat (xT)",
  "possession_value.vaep_total": "VAEP total",
  "tempo.direct_attacks": "Direct attacks",
  "tempo.buildup_attacks": "Build-up attacks",
  "tempo.passes_per_sequence": "Passes per sequence",
  "tempo.sequence_time": "Sequence time (s)",
  "tempo.direct_speed": "Direct speed",
};

const HINTS = {
  "defending.ppda":
    "Passes allowed per defensive action. Lower generally indicates more intense pressing. Provider-defined.",
  "territory.field_tilt":
    "Share of final-third possession activity, 0-100. Provider-defined.",
  "territory.possession_pct":
    "May reflect pass share rather than time on the ball. Provider-defined.",
  "territory.avg_action_x":
    "Provider average x-position of the team's actions, 0-105 m. Assumed to share the acting-team frame used for shots and heatmaps (0 = own goal line); the bundle does not define it separately.",
  "carrying.progressive_carries": "Progressive carries may be synthesized by the provider.",
  progressive_carries: "Progressive carries may be synthesized by the provider.",
  xt_total: "Expected threat added by actions, provider model.",
  "possession_value.xt_total": "Expected threat added by actions, provider model.",
  xg_per_shot: "Total xG divided by shots in the sampled matches' source rows.",
};

// Keys on a 0-100 scale (verified against the cached bundle's value ranges).
const PERCENT_KEYS = new Set([
  "territory.possession_pct",
  "territory.field_tilt",
  "passing.pass_accuracy",
]);

const titleCase = (text) => text.charAt(0).toUpperCase() + text.slice(1);

export function metricLabel(key) {
  if (LABELS[key]) return LABELS[key];
  const tail = String(key).split(".").pop();
  return titleCase(tail.replace(/_/g, " "));
}

export function metricHint(key) {
  return HINTS[key] || "";
}

export function isPercentMetric(key) {
  return PERCENT_KEYS.has(key);
}

export function formatNumber(value, digits = 2) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  if (Number.isInteger(value)) return value.toLocaleString("en-GB");
  return value.toFixed(digits);
}

/** `fixed` forces two decimals; use it for means so 6 and 4.9 read as 6.00 and 4.90. */
export function formatMetric(key, value, fixed = false) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const text = !fixed && Number.isInteger(value) ? value.toLocaleString("en-GB") : value.toFixed(2);
  return isPercentMetric(key) ? `${text}%` : text;
}

const TEAM_GROUPS = [
  {
    id: "attack",
    title: "Attack",
    keys: [
      "xgf", "npxgf", "open_play_xgf", "set_piece_xgf", "shots_for", "xg_per_shot",
      "blocked_shots_for", "creation.chances_created", "creation.sca", "creation.gca",
      "creation.xag", "possession_value.xt_total",
    ],
  },
  {
    id: "defence",
    title: "Defence & pressing",
    keys: [
      "xga", "npxga", "shots_against", "blocked_shots_against", "defending.ppda",
      "defending.tackles", "defending.interceptions", "defending.clearances", "defending.blocks",
      "defending.high_turnovers", "defending.counterpress_regains_5s", "defending.aerials_won",
      "defending.avg_defensive_action_x",
    ],
  },
  {
    id: "possession",
    title: "Possession & territory",
    keys: [
      "territory.possession_pct", "territory.field_tilt", "territory.box_entries",
      "territory.final_third_entries", "territory.avg_action_x", "passing.passes",
      "passing.pass_accuracy", "passing.progressive_passes", "passing.passes_into_box",
      "carrying.progressive_carries", "carrying.carries_into_box",
      "carrying.carries_into_final_third", "carrying.take_ons", "carrying.take_ons_won",
    ],
  },
  {
    id: "style",
    title: "Tempo & style",
    keys: [
      "tempo.direct_attacks", "tempo.buildup_attacks", "tempo.passes_per_sequence",
      "tempo.sequence_time", "tempo.direct_speed", "passing.crosses", "passing.through_balls",
      "passing.switches",
    ],
  },
  {
    id: "setpieces",
    title: "Set pieces & discipline",
    keys: [
      "corners_for", "corners_against", "fouls_for", "fouls_against", "yellow_cards_for",
      "yellow_cards_against", "red_cards_for", "red_cards_against",
    ],
  },
];

const PLAYER_GROUPS = [
  {
    id: "attacking",
    title: "Attacking",
    keys: ["goals", "npxg", "total_shots", "assists", "xag", "xt_total"],
  },
  {
    id: "passing",
    title: "Passing & progression",
    keys: ["passes_attempted", "accurate_passes", "key_passes", "progressive_passes", "progressive_carries"],
  },
  {
    id: "defending",
    title: "Defending & ball winning",
    keys: ["tackles", "interceptions", "recoveries"],
  },
  {
    id: "discipline",
    title: "Discipline",
    keys: ["fouls", "yellow_cards", "red_cards"],
  },
];

/**
 * Groups the metrics actually present in `metrics` into labelled sections.
 * Metrics the provider returns that we have no curated group for are kept
 * in an "Other" section instead of being dropped.
 */
export function groupMetrics(kind, metrics = {}) {
  const defs = kind === "player" ? PLAYER_GROUPS : TEAM_GROUPS;
  const used = new Set();
  const groups = defs
    .map((def) => {
      const items = def.keys
        .filter((key) => metrics[key])
        .map((key) => {
          used.add(key);
          return { key, label: metricLabel(key), hint: metricHint(key), metric: metrics[key] };
        });
      return { id: def.id, title: def.title, items };
    })
    .filter((group) => group.items.length);

  const other = Object.keys(metrics)
    .filter((key) => !used.has(key))
    .sort()
    .map((key) => ({ key, label: metricLabel(key), hint: metricHint(key), metric: metrics[key] }));
  if (other.length) groups.push({ id: "other", title: "Other source metrics", items: other });
  return groups;
}

/* ------------------------------------------------------------------ */
/* Competition and match helpers                                        */
/* ------------------------------------------------------------------ */

export function competitionLabel(id, names = {}) {
  if (id === undefined || id === null || id === "") return "—";
  return names[id] || String(id);
}

/** 'W' | 'D' | 'L' from a team row, or null when a score is missing. */
export function resultOf(match) {
  const goals = match?.goals;
  const conceded = match?.conceded;
  if (typeof goals !== "number" || typeof conceded !== "number") return null;
  if (goals > conceded) return "W";
  if (goals < conceded) return "L";
  return "D";
}

export function formSummary(matches = []) {
  const summary = { W: 0, D: 0, L: 0, played: 0, goalsFor: 0, goalsAgainst: 0 };
  matches.forEach((match) => {
    const result = resultOf(match);
    if (!result) return;
    summary[result] += 1;
    summary.played += 1;
    summary.goalsFor += match.goals;
    summary.goalsAgainst += match.conceded;
  });
  return summary;
}

/** Mean of each requested metric over a subset of match rows (real values only). */
export function meanOf(matches, key) {
  const values = matches
    .map((match) => match.values?.[key])
    .filter((value) => typeof value === "number" && Number.isFinite(value));
  if (!values.length) return null;
  return { value: values.reduce((sum, v) => sum + v, 0) / values.length, sample: values.length };
}

/** Home / away splits of team match means. Rows without a venue flag are ignored. */
export function venueSplit(matches = [], keys = []) {
  const home = matches.filter((m) => m.home === true);
  const away = matches.filter((m) => m.home === false);
  return {
    homeCount: home.length,
    awayCount: away.length,
    rows: keys.map((key) => ({
      key,
      home: meanOf(home, key),
      away: meanOf(away, key),
    })),
  };
}

/* ------------------------------------------------------------------ */
/* Players                                                              */
/* ------------------------------------------------------------------ */

/** Appearance and minutes facts needed to interpret per-90 values honestly. */
export function exposureSummary(matches = []) {
  const withMinutes = matches.filter((m) => typeof m.minutes === "number" && m.minutes > 0);
  const flagged = matches.filter((m) => m.advanced_minutes_compatible === false);
  const minutes = withMinutes.reduce((sum, m) => sum + m.minutes, 0);
  return {
    appearances: matches.length,
    withMinutes: withMinutes.length,
    minutes,
    advancedIncompatible: flagged.length,
    smallSample: minutes < 450,
  };
}

/* ------------------------------------------------------------------ */
/* Shots                                                                */
/* ------------------------------------------------------------------ */

export const OUTCOME_LABELS = {
  Goal: "Goal",
  AttemptSaved: "Saved",
  Miss: "Off target",
  Post: "Woodwork",
};

export function outcomeLabel(outcome) {
  if (!outcome) return "Unknown";
  if (OUTCOME_LABELS[outcome]) return OUTCOME_LABELS[outcome];
  if (/block/i.test(outcome)) return "Blocked";
  return String(outcome).replace(/([a-z])([A-Z])/g, "$1 $2");
}

/** Visual class so colour is never the only cue (shape/stroke also differ). */
export function outcomeKind(outcome) {
  if (outcome === "Goal") return "goal";
  if (/block/i.test(outcome || "")) return "blocked";
  if (outcome === "AttemptSaved") return "saved";
  if (outcome === "Post") return "post";
  if (outcome === "Miss") return "miss";
  return "other";
}

const BODY_LABELS = { Header: "Header", LeftFoot: "Left foot", RightFoot: "Right foot", OtherBodyParts: "Other" };
export const bodyPartLabel = (value) => (value ? BODY_LABELS[value] || String(value) : "Unknown");

const CONTEXT_LABELS = {
  RegularPlay: "Open play",
  FastBreak: "Fast break",
  SetPiece: "Set piece",
  FromCorner: "From corner",
  FreeKick: "Free kick",
  Penalty: "Penalty",
  IndividualPlay: "Individual play",
  ThrowInSetPiece: "Throw-in",
};
export const contextLabel = (value) => (value ? CONTEXT_LABELS[value] || String(value) : "Unknown");

export function hasLocation(shot) {
  return (
    Number.isFinite(shot?.x) &&
    Number.isFinite(shot?.y) &&
    shot.x >= 0 && shot.x <= PITCH.length &&
    shot.y >= 0 && shot.y <= PITCH.width
  );
}

export function isInPenaltyArea(shot) {
  return hasLocation(shot) && shot.x >= BOX.minX && shot.y >= BOX.minY && shot.y <= BOX.maxY;
}

/** Shots on the far side get mirrored so both teams can share one pitch. */
export function mirrorShot(shot) {
  return { ...shot, x: PITCH.length - shot.x, y: PITCH.width - shot.y };
}

export function distinctValues(shots, field) {
  return [...new Set(shots.map((shot) => shot[field]).filter((v) => v !== undefined && v !== null))].sort();
}

export function filterShots(shots = [], filters = {}) {
  return shots.filter(
    (shot) =>
      (!filters.outcome || shot.outcome === filters.outcome) &&
      (!filters.body_part || shot.body_part === filters.body_part) &&
      (!filters.context || shot.context === filters.context) &&
      (!filters.fixture_id || String(shot.fixture_id) === String(filters.fixture_id)) &&
      (!filters.boxOnly || isInPenaltyArea(shot))
  );
}

export function summariseShots(shots = []) {
  const withXg = shots.filter((shot) => typeof shot.xg === "number" && Number.isFinite(shot.xg));
  const goals = shots.filter((shot) => shot.outcome === "Goal").length;
  const xg = withXg.reduce((sum, shot) => sum + shot.xg, 0);
  const box = shots.filter(isInPenaltyArea);
  return {
    count: shots.length,
    goals,
    xg: withXg.length ? xg : null,
    xgPerShot: withXg.length ? xg / withXg.length : null,
    goalsMinusXg: withXg.length === shots.length && shots.length ? goals - xg : null,
    inBox: box.length,
    xgCoverage: withXg.length,
  };
}

/** Counts per category (label -> count), sorted by count descending. */
export function countBy(shots, field, labeller = (v) => v) {
  const counts = new Map();
  shots.forEach((shot) => {
    const key = labeller(shot[field]);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

/* ------------------------------------------------------------------ */
/* Heatmaps                                                             */
/* ------------------------------------------------------------------ */

const sameGrid = (a, b) =>
  a?.grid?.length === b?.grid?.length && a?.grid?.width === b?.grid?.width && a?.grid?.frame === b?.grid?.frame;

/**
 * Sums observed action counts per cell across matches that share an identical
 * grid and orientation. Returns null when nothing can be combined.
 */
export function aggregateHeatmaps(heatmaps = []) {
  const usable = heatmaps.filter((h) => h?.grid?.frame === "acting_ltr" && h.grid.length > 0 && h.grid.width > 0);
  if (!usable.length) return null;
  const first = usable[0];
  const compatible = usable.filter((h) => sameGrid(first, h));
  const cells = new Map();
  let actions = 0;
  let actionsKnown = true;
  compatible.forEach((heat) => {
    heat.cells.forEach(([x, y, value]) => {
      const key = `${x}:${y}`;
      cells.set(key, (cells.get(key) || 0) + value);
    });
    if (typeof heat.actions === "number") actions += heat.actions;
    else actionsKnown = false;
  });
  return {
    grid: first.grid,
    cells: [...cells.entries()].map(([key, value]) => {
      const [x, y] = key.split(":").map(Number);
      return [x, y, value];
    }),
    actions: actionsKnown ? actions : null,
    matches: compatible.length,
    skipped: usable.length - compatible.length,
  };
}

/** Share of observed actions in the defensive / middle / attacking third (by grid column). */
export function heatmapThirds(heat) {
  if (!heat?.grid?.length || !heat.cells?.length) return null;
  const length = heat.grid.length;
  const sums = [0, 0, 0];
  heat.cells.forEach(([x, , value]) => {
    const third = Math.min(2, Math.floor((x / length) * 3));
    sums[third] += value;
  });
  const total = sums.reduce((a, b) => a + b, 0);
  if (!total) return null;
  return { defensive: sums[0] / total, middle: sums[1] / total, attacking: sums[2] / total, total };
}

/* ------------------------------------------------------------------ */
/* Passing networks                                                     */
/* ------------------------------------------------------------------ */

export function networkTopLinks(network, limit = 8) {
  if (!network?.edges?.length) return [];
  return [...network.edges].sort((a, b) => b.passes - a.passes).slice(0, limit);
}

/**
 * Combines the provider's directed edges into one row per player pair
 * (total passes plus each direction), sorted by total descending.
 */
export function networkPairs(network) {
  const pairs = new Map();
  (network?.edges || []).forEach((edge) => {
    const key = [edge.from.id, edge.to.id].sort().join("|");
    const pair = pairs.get(key) || { key, a: edge.from, b: edge.to, total: 0, ab: 0, ba: 0 };
    pair.total += edge.passes;
    if (edge.from.id === pair.a.id) pair.ab += edge.passes;
    else pair.ba += edge.passes;
    pairs.set(key, pair);
  });
  return [...pairs.values()].sort((x, y) => y.total - x.total);
}

/** Smallest threshold that keeps at most `target` edges, so default views stay legible. */
export function defaultEdgeThreshold(network, target = 24) {
  const passes = (network?.edges || []).map((e) => e.passes).sort((a, b) => b - a);
  if (passes.length <= target) return passes.length ? Math.min(...passes) : 0;
  return passes[target - 1];
}

/* ------------------------------------------------------------------ */
/* xG timeline                                                          */
/* ------------------------------------------------------------------ */

/**
 * Cumulative xG step series per team from timed shots.
 * `goals` joins shot ids to located shots that ended as goals, when available.
 */
export function cumulativeXg(timeline = [], teamIds = [], shots = []) {
  const goalIds = new Set(shots.filter((s) => s.outcome === "Goal").map((s) => s.id));
  const sorted = [...timeline].sort((a, b) => a.minute - b.minute);
  const series = {};
  teamIds.forEach((teamId) => {
    let total = 0;
    series[teamId] = sorted
      .filter((shot) => shot.team_id === teamId)
      .map((shot) => {
        total += shot.xg;
        return { minute: shot.minute, xg: shot.xg, total, goal: goalIds.has(shot.id), id: shot.id };
      });
  });
  const lastMinute = sorted.length ? sorted[sorted.length - 1].minute : 0;
  return { series, lastMinute: Math.max(90, Math.ceil(lastMinute)) };
}

/* ------------------------------------------------------------------ */
/* Comparison bars                                                      */
/* ------------------------------------------------------------------ */

/** Share (0-1) of A in A+B for mirrored bars; null if either side is missing/negative. */
export function mirrorShare(a, b) {
  if (typeof a !== "number" || typeof b !== "number" || a < 0 || b < 0) return null;
  const total = a + b;
  return total === 0 ? 0.5 : a / total;
}

/* ------------------------------------------------------------------ */
/* Export                                                               */
/* ------------------------------------------------------------------ */

const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function toCsv(rows, columns) {
  const header = columns.map((c) => csvCell(c.label)).join(",");
  const lines = rows.map((row) => columns.map((c) => csvCell(c.value(row))).join(","));
  return [header, ...lines].join("\n");
}
